import { AppLauncher } from '@capacitor/app-launcher';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { DownloadIcon } from 'lucide-react';

import { AppBootSplash } from '@/components/app-boot-splash';
import { Button } from '@/components/ui/button';

import { useCheckAppVersion } from '../api/check-app-version';

const nativeStoreUrl = (url: string): string => {
  const platform = Capacitor.getPlatform();

  if (platform === 'ios') {
    return url.replace(
      /^https:\/\/apps\.apple\.com\//,
      'itms-apps://apps.apple.com/'
    );
  }

  if (platform === 'android') {
    const parsed = new URL(url);
    const appId = parsed.searchParams.get('id');
    if (appId) return `market://details?id=${encodeURIComponent(appId)}`;
  }

  return url;
};

const openStore = async (url: string) => {
  if (Capacitor.isNativePlatform()) {
    try {
      const { completed } = await AppLauncher.openUrl({
        url: nativeStoreUrl(url),
      });
      if (completed) return;
    } catch {
      // Fall through to a web URL if the store app is unavailable.
    }
  }

  try {
    await Browser.open({ url });
  } catch {
    // Web, or the plugin is unavailable.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Blocks an out-of-date native build.
 *
 * Mounted above RouterProvider: a blocked build must be stopped before route
 * loaders or sign-in can run, and the block has to survive navigation.
 *
 * `update_required` renders a full-screen, non-dismissible screen. There is
 * deliberately no escape — the whole point is that this build can no longer
 * talk to the API safely, and a "continue anyway" button would be used.
 *
 * The router stays hidden for the initial native check. Resume checks cover
 * the current screen without unmounting it, then block it if an update is required.
 * A failed check still fails open: a network outage must not strand a current
 * binary behind a screen it cannot escape.
 */
export function AppVersionGate({ children }: { children: React.ReactNode }) {
  const { check, isChecking, isRechecking } = useCheckAppVersion();

  const status = check?.status;
  const storeUrl = check?.storeUrl ?? null;
  const latestVersion = check?.latestVersion ?? check?.minimumVersion ?? null;

  if (status === 'update_required') {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-background p-8 text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-muted">
          <DownloadIcon className="size-7 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">Update required</h1>
          <p className="max-w-sm text-muted-foreground">
            This version of Borrah is no longer supported. Update to the latest
            version to keep going.
          </p>
        </div>
        {storeUrl && (
          <Button size="lg" onClick={() => void openStore(storeUrl)}>
            <DownloadIcon />
            Update now
          </Button>
        )}
        {check?.currentVersion && latestVersion && (
          <dl className="grid w-full max-w-sm grid-cols-2 divide-x rounded-xl border bg-muted/40 py-3">
            <div className="space-y-1 px-4">
              <dt className="text-xs text-muted-foreground">Current version</dt>
              <dd className="font-mono text-sm font-medium">
                {check.currentVersion}
              </dd>
            </div>
            <div className="space-y-1 px-4">
              <dt className="text-xs text-muted-foreground">Latest version</dt>
              <dd className="font-mono text-sm font-medium">{latestVersion}</dd>
            </div>
          </dl>
        )}
      </div>
    );
  }

  if (isChecking) return <AppBootSplash />;

  return (
    <>
      {children}
      {isRechecking && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/60"
          role="status"
          aria-label="Checking app version"
        >
          <span className="rounded-md bg-background px-4 py-2 text-sm text-muted-foreground shadow">
            Checking app version…
          </span>
        </div>
      )}
    </>
  );
}
